/**
 * `client-archive` (crossmarc/steward-app#1863): the firm's own record that it removed a
 * sponsored client ("Remove client" on the client profile), in the firm's own workspace. One row
 * per `advisor-client` record ({@link ADVISOR_CLIENT_TYPE_CODE}, `advisorClient.ts`), found by
 * its `clientId` attribute.
 *
 * ## Why it holds a display name
 *
 * Removing a client ends the firm's sponsorship, which removes the firm's advisors from the
 * family's workspace. The Clients table is built from that roster, so the family's row goes with
 * it, and nothing else the firm holds keeps the name it knew the family by in one place. This row
 * keeps it, for the Archived view (a follow-up to #1863; this type ships first so the rows exist
 * when the view does).
 *
 * ## Shape, modelled on `onboarding-mark` (`onboardingMark.ts`)
 *
 * Same pack (`STEWARD_ADVISOR_FIRM_TYPE_DEFS`), same write shape: the façade creates it under the
 * firm admin's own bearer token (`services/steward-app-api/src/handlers/clientRemove.ts`), never a
 * background role, and `accessControl.writeCapability` is `STEWARD_WRITE_CAPABILITY`. No new
 * capability, role, actor rule or appcore change. `strict` schema, and every field a flat string:
 * appcore compiles a strict `array` field as `z.array(z.string())` and has no object kind
 * (crossmarc/steward-app#1859), which `packTypeDefs.test.ts` pins.
 *
 * ## Written once
 *
 * A repeat of Remove client finds the existing row and leaves it as it is, so the row records the
 * FIRST removal. The façade never calls `updateEntity` on this type.
 *
 * ## Deleted by a permanent delete
 *
 * It declares `clientId`, so `clientPurgeManifest.test.ts` requires the permanent delete to reach
 * it: `services/clientPurge.ts` deletes every row for the client (`purgeClientArchiveRows`).
 * Keeping a name after "permanently delete everything your firm holds" would break that promise.
 */
import { z } from 'zod';

export const CLIENT_ARCHIVE_TYPE_CODE = 'client-archive';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();
/** Cleaned at the façade (`safeDisplayText`, whitespace collapsed, trimmed) before it is stored. */
const displayText = z.string().min(1).max(200);

/** The STORED row. Every field is a flat string (#1859). */
export const ClientArchiveAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this archive belongs to (that type's own appcore `instanceId`). */
  clientId: id,
  /** The family workspace whose sponsorship the removal ended. */
  clientWorkspaceId: id,
  /** The name the firm knew the client by at removal time. */
  displayName: displayText,
  archivedAt: instant,
  archivedByUserId: id,
  archivedByName: displayText,
});

export type ClientArchiveAttributes = z.infer<typeof ClientArchiveAttributesSchema>;

/** Reads a stored `client-archive` row. `unreadable` covers "does not parse" and "is missing". */
export function readClientArchive(
  value: unknown,
): { kind: 'archive'; archive: ClientArchiveAttributes } | { kind: 'unreadable' } {
  const parsed = ClientArchiveAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'archive', archive: parsed.data } : { kind: 'unreadable' };
}

/**
 * Every user-facing string for Remove client (crossmarc/steward-app#1863): the SPA's menu item and
 * confirm dialog, and the family's email. One object, so the set can be reviewed and swapped in one
 * place. Drafts from the #1863 brief (2026-09-26); the email strings and the dialog body carry
 * Matt's "Steward workspace" wording from the same day. The email does not say "reply to this
 * email": it is sent from a `no-reply@` address with no Reply-To (`deploy/lib/api-stack.ts`'s
 * `INVITE_FROM_EMAIL`), so a reply reaches no one. No em or en dashes
 * (CLAUDE.md copy rules): `clientArchive.test.ts` checks every string.
 *
 * `firm` and `name` are inserted as given: the email renderer passes a firm name already cleaned
 * by `mailFirmName` and escapes the HTML itself, and the SPA renders these as React text.
 *
 * ## One copy, for an accepted family only (crossmarc/steward-app#1866)
 *
 * A first pass at #1866 added a second, "invitation withdrawn" pair for a family that never
 * accepted. Matt's ruling on the issue (2026-09-28) replaced that with a simpler rule: when Remove
 * only cancels an invitation the family never accepted, `clientRemove.ts` sends NO email at all,
 * so `emailSubject`/`emailBody` below describe the one case that still mails anyone, an ended
 * sponsorship the family HAD accepted. There is no second pair here any more; do not re-add one
 * without a fresh ruling.
 */
export const REMOVE_CLIENT_COPY = {
  menuItem: 'Remove client',
  dialogTitle: (name: string) => `Remove ${name}?`,
  dialogBody: (name: string, firm: string) =>
    `${name} will keep their Steward workspace and everything in it. ${firm} will no longer be able to see it, and this client will move to Archived. We'll let ${name} know by email.`,
  confirm: 'Remove client',
  cancel: 'Cancel',
  error: "That didn't work. Please try again, or contact us if it keeps happening.",
  emailSubject: (firm: string) => `${firm} is no longer connected to your Steward workspace`,
  emailBody: (firm: string) =>
    `${firm} has ended its connection to your Steward workspace. Your workspace and everything in it stays yours, just as it was. If this is unexpected, please contact ${firm} directly.`,
} as const;

/**
 * Every user-facing string for Remove client on a CONNECTED family (crossmarc/steward-app#1865),
 * one who brought their own Steward workspace: the firm's confirm dialog and result, and the
 * notice the family sees on People > Advisors. No em or en dashes (CLAUDE.md copy rules):
 * `connectedClientCopy.test.ts` checks every string.
 *
 * What the result may say is bounded by what the façade can know (`clientRemoveConnected.ts`):
 * how many advisors appcore removed, who it kept because the family made them admins, and whether
 * the caller still has access. appcore removes only the advisors it RECORDED joining through this
 * connection (its appcore#561 anchors). Firm people it has no record of exist on connections of
 * every age, not only old ones: staff who were never listed and grants the family re-made by hand.
 * (An invitation the family sent that names this firm is NOT in that list: since appcore#774 its
 * accept is refused with `INVITE_ENGAGEMENT_ENDED` once the connection has ended. An ordinary
 * Members invitation to a firm colleague carries no firm label and still works, which is why
 * the dialog says "anyone else who has access" and does not name invitations either way.) So no
 * string attributes "not removed" to a connection's age; each says only "Steward had no current
 * record" and that anyone else keeps access until the family removes them. No email is sent (founder ruling 2026-09-28), and
 * the dialog says so without promising when the family will see it.
 */
export const REMOVE_CONNECTED_CLIENT_COPY = {
  menuItem: 'Remove client',
  dialogTitle: (name: string) => `Remove ${name}?`,
  dialogBody: (name: string, firm: string) =>
    `${name} will keep their Steward workspace and everything in it. ${firm}'s connection to it will end, and they will leave your client list. Advisors Steward recorded joining through this connection will lose access, unless the family made them an admin. Anyone else from ${firm} who has access keeps it until the family removes them.`,
  dialogNoEmail:
    "We won't email them. An admin of their workspace can see that the connection ended in their Steward settings.",
  confirm: 'Remove client',
  cancel: 'Cancel',
  close: 'Close',
  error: "That didn't work. Please try again, or contact us if it keeps happening.",
  resultTitle: (name: string) => `${name} has been removed`,
  alreadyRemoved: (name: string) => `${name} was already removed from your firm.`,
  removedCount: (count: number) => (count === 1
    ? '1 advisor from your firm no longer has access to their workspace.'
    : `${count} advisors from your firm no longer have access to their workspace.`),
  keptIntro: 'The family made these people admins, so they keep access. Only the family can remove them:',
  keptYou: 'You',
  keptUnnamed: 'Someone from your firm',
  noneRemoved:
    'Steward had no current record of anyone from your firm joining through this connection, so no one was removed. Anyone from your firm who still has access keeps it until the family removes them.',
  /** After `removedCount` or the kept list: the people appcore had no record of. */
  othersKeepAccess: 'Anyone else from your firm who still has access keeps it until the family removes them.',
  /** After `alreadyRemoved` when this call removed and kept nobody. */
  anyoneKeepsAccess: 'Anyone from your firm who still has access keeps it until the family removes them.',
  youStillHaveAccess: 'You still have access to their workspace.',
  familyNotice: (firm: string, date: string) =>
    `${firm} ended its connection to your Steward workspace on ${date}. Your workspace and everything in it stays yours. Anyone from ${firm} who still has access to your workspace appears under Members, where you can remove them.`,
} as const;
